import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFormContext, useWatch } from 'react-hook-form';

import { postConversionConfirmApi } from '@flipster/apis/v1/wallet';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui';

import { CurrencyDisplay } from '../../../../../misc/display';
import { showErrorDialog } from '../../../../show-error-dialog';
import { ConvertFormSchema } from '../../shared/validation';
import { convertModalBunja } from '../../state';
import { CountDownSec } from '../1-form/count-down-sec';

export const Preview = () => {
  const { control, getValues } = useFormContext<ConvertFormSchema>();

  const { fromCurrency, toCurrency, initFromAmount, initToAmount, initExchangeRate } = useWatch({ control });

  const {
    steps: { pushAtom },
    precisionAtom,
  } = useBunja(convertModalBunja);

  const push = useSetAtom(pushAtom);
  const { from: fromPrecision, to: toPrecision } = useAtomValue(precisionAtom);

  const onTimeOver = () => {
    snackbar.error({
      message: <FormattedMessage id='Convert.r250401.The_conversion_has_been_canceled_because_the_process_timed_out' />,
    });
    push({ step: 'form', context: undefined });
  };

  const [isLoading, onSubmit] = useSendApi(async () => {
    const currencyConversionId = getValues('currencyConversionId');
    if (!currencyConversionId) return;
    try {
      await postConversionConfirmApi({ currencyConversionId });
      push({ step: 'complete', context: undefined });
    } catch (err) {
      showErrorDialog(err);
    }
  });

  return (
    <div className={css({ textStyle: 'Legacy/Body2', color: 'legacy.gray.d' })}>
      <dl
        className={css({
          mb: 24,
          '& dt': {
            textStyle: 'Legacy/Body3',
            mb: 8,
            color: 'legacy.gray.8',
          },
          '& dd': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
          '& dd + dt': {
            mt: 24,
          },
        })}
      >
        <dt>
          <FormattedMessage id='Convert.r250401.From' />
        </dt>
        <dd>
          <CurrencyDisplay
            currency={fromCurrency!}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
          />
          <Price price={initFromAmount} currency={fromCurrency} precision={fromPrecision} toFixed />
        </dd>
        <dt>
          <FormattedMessage id='Deposit.r250401.To' />
        </dt>
        <dd>
          <CurrencyDisplay
            currency={toCurrency!}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
          />
          <Price price={initToAmount} currency={toCurrency} precision={toPrecision} toFixed />
        </dd>
        <hr
          className={css({
            h: 1,
            my: 24,
            bg: 'legacy.gray.4',
            border: 'unset',
          })}
        />
        <div
          className={hstack({
            justify: 'space-between',
            '& dt': { mb: 0 },
          })}
        >
          <dt>
            <FormattedMessage id='Convert.r250401.Exchange_rate' />
          </dt>
          <dd>
            <Price fontType='num3' price={initExchangeRate} precision={toPrecision} currency={toCurrency} toFixed />
          </dd>
        </div>
      </dl>
      <div className={center()}>
        <CountDownSec seconds={5} onComplete={onTimeOver} />
      </div>
      <footer>
        <Button
          onClick={() => push({ step: 'form', context: undefined })}
          label={<FormattedMessage id='Assets.r250401.Cancel' />}
          size='large'
          hierarchy='secondary'
          color='neutral'
        />
        <Button
          onClick={onSubmit}
          disabled={isLoading}
          label={<FormattedMessage id='Assets.r250401.Convert' />}
          size='large'
        />
      </footer>
    </div>
  );
};
